const STORAGE_KEY = 'nur.notification.preferences';
const DEFAULTS = {
  messages: true,
  calls: true,
  platform: true,
  sound: true,
  quietHours: { enabled: false, from: '22:00', to: '08:00' }
};

let audioContext = null;
let armed = false;
let lastPlayedAt = 0;

function preferences() {
  try {
    const stored = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
    return {
      ...DEFAULTS,
      ...stored,
      quietHours: { ...DEFAULTS.quietHours, ...(stored.quietHours || {}) }
    };
  } catch {
    return DEFAULTS;
  }
}

function minutes(value) {
  const [hours, mins] = String(value || '00:00').split(':').map(Number);
  return (hours * 60) + mins;
}

function quietNow(settings, date = new Date()) {
  if (!settings.quietHours?.enabled) return false;
  const now = (date.getHours() * 60) + date.getMinutes();
  const from = minutes(settings.quietHours.from);
  const to = minutes(settings.quietHours.to);
  return from <= to ? now >= from && now < to : now >= from || now < to;
}

function context() {
  if (audioContext) return audioContext;
  const AudioContext = window.AudioContext || window.webkitAudioContext;
  if (!AudioContext) return null;
  audioContext = new AudioContext();
  return audioContext;
}

function tone(ctx, destination, { frequency, start, duration, volume, type = 'sine' }) {
  const oscillator = ctx.createOscillator();
  const gain = ctx.createGain();
  oscillator.type = type;
  oscillator.frequency.setValueAtTime(frequency, start);
  gain.gain.setValueAtTime(0.0001, start);
  gain.gain.exponentialRampToValueAtTime(volume, start + 0.025);
  gain.gain.exponentialRampToValueAtTime(0.0001, start + duration);
  oscillator.connect(gain);
  gain.connect(destination);
  oscillator.start(start);
  oscillator.stop(start + duration + 0.02);
}

export function cacheNotificationPreferences(value) {
  const normalized = {
    ...DEFAULTS,
    ...(value || {}),
    quietHours: { ...DEFAULTS.quietHours, ...(value?.quietHours || {}) }
  };
  localStorage.setItem(STORAGE_KEY, JSON.stringify(normalized));
  return normalized;
}

export function notificationSoundAllowed() {
  const settings = preferences();
  return settings.sound !== false && !quietNow(settings);
}

export function notificationPreferenceEnabled(key = 'platform') {
  const settings = preferences();
  return settings[key] !== false && !quietNow(settings);
}

export async function unlockNotificationSound() {
  const ctx = context();
  if (!ctx) return false;
  if (ctx.state === 'suspended') await ctx.resume().catch(() => {});
  return ctx.state === 'running';
}

export function armNotificationSound() {
  if (armed || typeof window === 'undefined') return;
  armed = true;
  const unlock = () => {
    void unlockNotificationSound();
    window.removeEventListener('pointerdown', unlock, true);
    window.removeEventListener('keydown', unlock, true);
  };
  window.addEventListener('pointerdown', unlock, { capture: true, once: true });
  window.addEventListener('keydown', unlock, { capture: true, once: true });
}

export async function playNotificationSound(kind = 'default', { force = false } = {}) {
  if (!force && !notificationSoundAllowed()) return false;
  const now = Date.now();
  if (!force && now - lastPlayedAt < 650) return false;
  const ctx = context();
  if (!ctx) return false;
  if (ctx.state === 'suspended') await ctx.resume().catch(() => {});
  if (ctx.state !== 'running') return false;
  lastPlayedAt = now;

  const start = ctx.currentTime + 0.015;
  const master = ctx.createGain();
  master.gain.setValueAtTime(0.9, start);
  master.connect(ctx.destination);

  if (kind === 'urgent' || kind === 'call' || kind === 'temperature') {
    tone(ctx, master, { frequency: 659.25, start, duration: 0.22, volume: 0.055 });
    tone(ctx, master, { frequency: 783.99, start: start + 0.18, duration: 0.28, volume: 0.048 });
  } else {
    tone(ctx, master, { frequency: 659.25, start, duration: 0.2, volume: 0.045 });
    tone(ctx, master, { frequency: 880, start: start + 0.13, duration: 0.3, volume: 0.038 });
  }
  window.setTimeout(() => master.disconnect(), 700);
  return true;
}
